<template>
  <!--
    End-user viewer for htmljs documents (engine === 'htmljs'). Deliberately SEPARATE from the legacy
    FTSDocumentModal (PdfSharp/PDF.js) so the new path can evolve without touching the legacy viewer —
    routing branches on the document's `engine` before any legacy dispatch runs.

    Preview renders live and fully client-side through FTSHtmlJsRenderer (sandboxed iframe, same
    assembleHost + runtime the backend render service uses -> on-screen == the exported PDF). Print
    goes through the browser's own render (renderer.print(), the Print / "Save as PDF" dialog).
    Download saves the server-rendered PDF from /doc/render as a file (SS05938); when the Chromium
    render service cannot produce it, the toolbar says so and offers "Print instead" rather than
    silently opening a print dialog (SS05906's 502). A bulk selection keeps the combined print job.
  -->
  <fts-client-modal
    width="1080px"
    :full-screen="expanded"
    append-content-class="htmljs-document-modal__body"
    append-container-class="document-viewer-container"
    class="fts-htmljs-document-modal"
    @closed="reset"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['fas', 'file-alt']" fixed-width />
    <!-- header:title — document name + the entry id it was rendered for -->
    <template #title>
      <div class="htmljs-doc-title">
        <span class="htmljs-doc-title__name">{{ displayName }}</span>
        <span
          v-if="isBulk"
          class="htmljs-doc-title__id"
          :title="items.length + ' entries selected'"
        >{{ items.length }} entries</span>
        <span v-else-if="isCollection" class="htmljs-doc-title__id" title="Report over many entries">report</span>
        <span v-else-if="entryId" class="htmljs-doc-title__id" :title="'Entry ID: ' + entryId">#{{ entryId }}</span>
      </div>
    </template>
    <!-- header:actions — expand-to-fill toggle, beside the close button -->
    <template #actions>
      <fts-btn
        text
        icon
        :title="expanded ? 'Exit full screen' : 'Expand to fill'"
        @click="expanded = !expanded"
      >
        <font-awesome-icon :icon="['fas', expanded ? 'compress' : 'expand']" />
      </fts-btn>
    </template>
    <!-- body -->
    <template #body>
      <!-- action toolbar, pinned just below the title -->
      <div class="htmljs-doc-toolbar">
        <fts-btn
          text
          :disabled="loading || !bundle"
          @click="print"
          title="Print / Save as PDF"
        >
          <font-awesome-icon :icon="['fas', 'print']" />
          <span>Print</span>
        </fts-btn>
        <fts-btn
          text
          :disabled="loading || downloading || !bundle"
          @click="download"
          :title="isBulk ? 'Print / Save all ' + items.length + ' as one PDF' : 'Download the PDF file'"
        >
          <font-awesome-icon :icon="['fas', 'download']" />
          <span>{{ downloading ? "Preparing..." : "Download" }}</span>
        </fts-btn>
        <!-- Bulk: the preview renders only the first entry; Print/Download produces all N combined. -->
        <span v-if="isBulk && !loading && bundle" class="htmljs-doc-bulk-note">
          Previewing 1 of {{ items.length }} — Print / Download produces all {{ items.length }}.
        </span>
        <!-- A download the render service could not produce: say so, and offer the client print. -->
        <span v-if="downloadError" class="htmljs-doc-download-error" role="alert">
          <span>Download failed: {{ downloadError }}</span>
          <a href="#" class="htmljs-doc-print-instead" @click.prevent="printInstead">Print instead</a>
        </span>
      </div>
      <!-- preview -->
      <div class="htmljs-doc-view">
        <!-- loading indicator -->
        <div class="loading-indicator" v-if="loading">
          <fts-spinner show />
          <div class="loading-indicator-label">Getting document...</div>
        </div>
        <!-- error -->
        <div class="document-error" v-else-if="error">
          <div class="document-error-label">Error</div>
          <div class="document-error-message">{{ error }}</div>
        </div>
        <!-- live preview -->
        <fts-html-js-renderer
          v-else-if="bundle"
          ref="renderer"
          :bundle="bundle"
          :data="data"
          :page="page"
          height="100%"
        />
      </div>
    </template>
  </fts-client-modal>
</template>

<script>
import { clientStore } from "../store/clientStore";

import FTSClientModal from "./FTSClientModal";
import FTSBtn from "./FTSBtn";
import FTSSpinner from "./FTSSpinner";
import FTSHtmlJsRenderer from "./FTSHtmlJsRenderer";

export default {
  name: "FTSHtmlJsDocumentModal",
  components: {
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner,
    "fts-html-js-renderer": FTSHtmlJsRenderer,
  },
  data: () => ({
    // Render params resolved from the showPDFDocument payload.
    moduleId: null,
    listingId: null,
    documentId: null,
    templateRef: null,
    entryId: null,
    // Bulk print (§5d, Flavor A): the full selection, whether this invocation is a bulk batch, and
    // the per-entry data models fetched for it. isBulk = a single-entry htmljs doc invoked with >1
    // selected rows (a collection/dataSource:"Entries" doc is the §5e path, not this).
    items: [],
    isBulk: false,
    // Collection/report document (dataSource "Entries"): renders ALL matching entries as one
    // document (entries[]) rather than one-per-record. `filters` carries the listing's active filter.
    isCollection: false,
    filters: null,
    dataModels: null,
    // Fetched pieces. `fileName` is the document's name -- the fallback for the file name the
    // server evaluates from the document's native file-name formula (data.template.fileName).
    fileName: "Document",
    // The document's user-input form values (hasForm -> FTSDocumentFormModal -> payload.inputs),
    // and - for a report that allows selection - the ticked rows it is restricted to.
    inputs: null,
    selectedIds: null,
    bundle: null,
    page: {},
    data: {},
    // UI state.
    loading: false,
    error: null,
    // Download (SS05938): the /doc/render request in flight, and why the last one failed.
    downloading: false,
    downloadError: null,
    // Expand-to-fill (full screen). Defaults on so the document has room and doesn't sit cramped
    // under the surrounding app chrome; the header toggle shrinks it back to the windowed 1080px.
    expanded: true,
  }),
  computed: {
    /** Title: the server-evaluated file name once the data is in, else the document's name. */
    displayName() {
      if (this.loading || this.isBulk) return this.fileName;
      return this._baseName();
    },
  },
  methods: {
    /**
     * `payload` is the standard showPDFDocument shape:
     *   { document:{ id, name, engine:'htmljs', templateRef, page, ... }, moduleId, listingId, items, filters }
     */
    async show(payload) {
      const doc = (payload && payload.document) || {};
      this.moduleId = payload && payload.moduleId;
      this.listingId = payload && payload.listingId;
      this.documentId = doc.id;
      this.templateRef = doc.templateRef;
      this.page = doc.page || {};
      this.fileName = doc.name || "Document";
      this.items = (payload && payload.items) || [];
      this.filters = (payload && payload.filters) || null;
      this.inputs = (payload && payload.inputs) || null;
      // A collection/report doc renders ALL matching entries as ONE document (§5e) and NEVER requires
      // a selection — it defaults to the whole list. Detect it from `dataSourceId` (what the listing
      // projection actually sends, WorkspaceController.List.cs) OR `dataSource` (the in-memory design
      // manifest); checking only `dataSource` missed real reports and wrongly demanded an entry.
      // A single-entry template with >1 selected rows is instead a bulk batch (Flavor A, §5d).
      this.isCollection = doc.dataSourceId === "collection" || doc.dataSource === "Entries";
      this.isBulk = !this.isCollection && this.items.length > 1;
      // A report that allows selection (`allowSelection`, the legacy locusIds branch) covers just the
      // ticked rows; the server still applies the listing, so a row the viewer cannot see stays out.
      this.selectedIds =
        this.isCollection && doc.allowSelection && this.items.length
          ? this.items.map((x) => x.id)
          : null;
      // The first selected/opened entry drives the single render (and the bulk on-screen preview).
      // A report ignores this — it uses the list, not a selected entry.
      this.entryId = this.items.length ? this.items[0].id : null;

      this.$refs.ftsClientModal.show();
      await this.load();
    },
    async load() {
      this.error = null;
      this.bundle = null;
      this.data = {};
      this.dataModels = null;
      this.loading = true;
      try {
        if (!this.isCollection && !this.entryId) {
          throw new Error("Select an entry to view this document.");
        }
        if (!this.templateRef) {
          throw new Error("This document has no saved template yet.");
        }
        // Bulk cap (§5d, decision 2026-07-09): each entry is a full sub-document, so bulk is capped
        // well below the collection row cap. Over the cap → block with a notice (don't silently drop).
        const BULK_MAX = 20;
        if (this.isBulk && this.items.length > BULK_MAX) {
          throw new Error(
            "Select up to " + BULK_MAX + " entries to print together (you selected " +
              this.items.length + ")."
          );
        }

        // Bundle once; data model(s) — one per entry for bulk, else just the opened entry.
        const [bundleRes] = await Promise.all([
          clientStore.dispatch("docBundleGet", this.templateRef),
          this._loadData(),
        ]);

        const bundleData = (bundleRes && bundleRes.data) || {};
        this.bundle = bundleData.bundle || null;
        // Prefer the page saved with the bundle; fall back to the manifest page from the header.
        if (bundleData.page && Object.keys(bundleData.page).length) {
          this.page = bundleData.page;
        }

        if (!this.bundle) {
          throw new Error("Template bundle is empty.");
        }
      } catch (err) {
        this.error = this._message(err);
      } finally {
        this.loading = false;
      }
    },
    // Fetch the typed data model(s). Bulk: one /doc/data per selected entry (each carries the
    // existing per-entry auth) → dataModels; the on-screen preview shows the first. Single: one model.
    async _loadData() {
      if (this.isCollection) {
        this.data = await this._fetchCollection();
      } else if (this.isBulk) {
        const models = await Promise.all(this.items.map((it) => this._fetchModel(it.id)));
        this.dataModels = models;
        this.data = models.length ? models[0] : {};
      } else {
        this.data = await this._fetchModel(this.entryId);
      }
    },
    // Report (dataSource "Entries"): one collection model (entries[]) for the whole listing. No
    // entryId — the backend reads the manifest's dataSourceId and applies the listing's own filter,
    // so the report shows exactly the rows this user already sees in that listing.
    async _fetchCollection() {
      const res = await clientStore.dispatch("docData", {
        moduleId: this.moduleId,
        documentId: this.documentId,
        listingId: this.listingId,
        filters: this.filters,
        entryIds: this.selectedIds,
        inputs: this.inputs,
      });
      return (res && res.data) || {};
    },
    async _fetchModel(entryId) {
      const res = await clientStore.dispatch("docData", {
        moduleId: this.moduleId,
        entryId,
        documentId: this.documentId,
        listingId: this.listingId,
        inputs: this.inputs,
      });
      return (res && res.data) || {};
    },
    print() {
      if (!this.$refs.renderer) return;
      // Bulk → one combined print job for all selected entries (Flavor A); else the single render.
      // Both are the fully client-side path (browser "Save as PDF", no /doc/render round-trip).
      if (this.isBulk && typeof this.$refs.renderer.printBatch === "function") {
        this.$refs.renderer.printBatch(this.dataModels, this.fileName);
      } else if (typeof this.$refs.renderer.print === "function") {
        // The print dialog's "Save as PDF" proposes the document title as the file name.
        this.$refs.renderer.print(this._baseName());
      }
    },
    // Download saves the server-rendered PDF (/doc/render) as <document name>.pdf (SS05938, ruled
    // 2026-09-14). If the render service cannot produce it (SS05906's 502, a timeout, an empty
    // body) the toolbar says why and offers "Print instead" - a download never opens a print dialog
    // on its own. A bulk selection keeps the combined client print job (A1): /doc/render renders one
    // entry per call.
    async download() {
      if (this.isBulk) {
        this.print();
        return;
      }
      if (this.downloading) return;
      this.downloading = true;
      this.downloadError = null;
      try {
        const res = await clientStore.dispatch("docRender", this._renderPayload());
        const blob = res && res.data;
        if (!(blob instanceof Blob) || !blob.size) {
          throw new Error("The server returned an empty file.");
        }
        this._saveBlob(blob, this._downloadName());
      } catch (err) {
        this.downloadError = await this._downloadMessage(err);
      } finally {
        this.downloading = false;
      }
    },
    printInstead() {
      this.downloadError = null;
      this.print();
    },
    // The same request /doc/data got for this document: the opened entry, or the listing (and its
    // active filter) for a report.
    _renderPayload() {
      const payload = {
        moduleId: this.moduleId,
        documentId: this.documentId,
        listingId: this.listingId,
      };
      if (this.isCollection) {
        payload.filters = this.filters;
        if (this.selectedIds) payload.entryIds = this.selectedIds;
      } else {
        payload.entryId = this.entryId;
      }
      if (this.inputs) payload.inputs = this.inputs;
      return payload;
    },
    // The name the server evaluated from the document's native file-name formula for this entry
    // (/doc/data's template.fileName -- the name an e-mail attachment of it carries), else the
    // document's name.
    _downloadName() {
      const fromServer = this.data && this.data.template && this.data.template.fileName;
      return fromServer || (this.fileName || "document") + ".pdf";
    },
    _baseName() {
      return this._downloadName().replace(/\.pdf$/i, "");
    },
    _saveBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // The browser reads the blob after click() returns; release it once the save has started.
      setTimeout(() => URL.revokeObjectURL(url), 40000);
    },
    // docRender asks for a Blob, so a failed render's JSON { error, detail } arrives as a Blob too.
    async _downloadMessage(err) {
      const data = err && err.response && err.response.data;
      if (data instanceof Blob) {
        try {
          const body = JSON.parse(await this._blobText(data));
          if (body && (body.error || body.message)) return body.error || body.message;
        } catch (e) {
          // not JSON - fall through to the generic message
        }
      }
      return this._message(err);
    },
    _blobText(blob) {
      if (typeof blob.text === "function") return blob.text();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
      });
    },
    _message(err) {
      // /doc/* problems come back as JSON { error, detail } (or a Blob when responseType was blob).
      if (err && err.response && err.response.data) {
        const d = err.response.data;
        if (typeof d === "string") return d;
        if (d.error || d.message) return d.error || d.message;
      }
      if (err && err.message) return err.message;
      return "Something went wrong. Please try again.";
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-htmljs-document-modal {
  .document-viewer-container {
    height: 100%;
    /* Flex column so the preview fills the exact body height — no blank gap below a short document,
       and a tall document scrolls inside the iframe instead of leaving dead space beneath it. */
    .htmljs-document-modal__body {
      padding: 0;
      background: var(--fts-card-background, #f4f5f7);
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }
  /* header title: document name + the entry id it belongs to */
  .htmljs-doc-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    height: 35px;
    .htmljs-doc-title__name {
      @include text-overflow-ellipsis;
      min-width: 0;
      font-weight: 500;
      font-size: 1.125rem;
      color: var(--fts-text);
    }
    .htmljs-doc-title__id {
      flex: none;
      font-size: 0.75rem;
      font-weight: 400;
      color: var(--fts-default);
    }
  }
  /* action toolbar, just below the header (fixed height; the preview takes the remaining space) */
  .htmljs-doc-toolbar {
    flex: none;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    background: var(--fts-card-background, #f4f5f7);
    border-bottom: 1px solid var(--fts-border);
  }
  .htmljs-doc-bulk-note {
    margin-left: 0.5rem;
    font-size: 0.75rem;
    color: var(--fts-default);
  }
  .htmljs-doc-download-error {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    margin-left: 0.5rem;
    font-size: 0.75rem;
    color: var(--fts-danger);
    > span {
      @include text-overflow-ellipsis;
      min-width: 0;
    }
    .htmljs-doc-print-instead {
      flex: none;
      font-weight: 500;
      color: var(--fts-primary);
      text-decoration: underline;
    }
  }
  .htmljs-doc-view {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
  }
  .fts-htmljs-renderer {
    display: block;
    height: 100%;
  }
  .loading-indicator {
    @include absolute-center-middle;
    z-index: 999;
    .loading-indicator-label {
      margin-top: 1rem;
    }
  }
  .document-error {
    @include absolute-center-middle;
    text-align: center;
    padding: 0.75rem 1rem;
    border-radius: 0.25rem;
    box-shadow: var(--fts-elevation-1);
    background-color: var(--fts-card-background);
    .document-error-label {
      margin-bottom: 0.125rem;
      font-weight: bold;
      font-size: 1rem;
      line-height: 1.5rem;
      color: var(--fts-danger);
    }
    .document-error-message {
      font-size: 0.875rem;
      line-height: 1.25rem;
      color: var(--fts-text);
    }
  }
}
</style>
